import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { JourneyProvider, useJourney, JOURNEY_STEPS } from './JourneyContext';
import AnimatedTimeline from './components/AnimatedTimeline';
import LivePreview from './components/LivePreview';
import DraftManager from './components/DraftManager';
import QuickSetupStep from './steps/QuickSetupStep';
import CompanyDetailsStep from './steps/CompanyDetailsStep';
import BranchSetupStep from './steps/BranchSetupStep';
import OnboardingConfigStep from './steps/OnboardingConfigStep';
import ReviewStep from './steps/ReviewStep';
import CelebrationScreen from './components/CelebrationScreen';
import { Button } from '../ui';
import { getDefaultBranch, getTemplateById } from './templates';

// Confirmation Modal Component
function ConfirmBranchesModal({ isOpen, onConfirm, onAddMore, branchCount }) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onAddMore} />
      
      {/* Modal */}
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-500 to-indigo-600 p-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h3 className="text-xl font-bold text-white">Confirm Branches</h3>
              <p className="text-white/80 text-sm">You have {branchCount} branch{branchCount !== 1 ? 'es' : ''} configured</p>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="p-6">
          <p className="text-gray-600 dark:text-gray-300 text-center mb-6">
            Have you added <span className="font-bold text-gray-900 dark:text-white">all the branches</span> for this company?
          </p>
          
          <div className="flex flex-col gap-3">
            <Button
              onClick={onConfirm}
              className="w-full justify-center bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700"
            >
              <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Yes, Continue to Next Step
            </Button>
            
            <Button
              variant="secondary"
              onClick={onAddMore}
              className="w-full justify-center border-2 border-blue-500 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30"
            >
              <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              No, Add More Branches
            </Button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

// Step components mapping
const STEP_COMPONENTS = {
  0: QuickSetupStep,
  1: CompanyDetailsStep,
  2: BranchSetupStep,
  3: OnboardingConfigStep,
  4: ReviewStep,
};

function JourneyWizardContent({ onComplete, onCancel }) {
  const {
    currentStep,
    completedSteps,
    nextStep,
    prevStep,
    goToStep,
    drafts,
    loadDraft,
    saveDraft,
    isDirty,
    journeyData,
    isLoading,
    branches,
    addBranch,
    selectedTemplate,
  } = useJourney();

  const [showDraftModal, setShowDraftModal] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [createdData, setCreatedData] = useState(null);
  const [validationError, setValidationError] = useState(null);
  const [stepValidators, setStepValidators] = useState({});
  const [showBranchConfirm, setShowBranchConfirm] = useState(false);

  // Check for existing drafts on mount
  useEffect(() => {
    if (drafts.length > 0 && !journeyData.draftId) {
      setShowDraftModal(true);
    }
  }, [drafts.length, journeyData.draftId]);

  const CurrentStepComponent = STEP_COMPONENTS[currentStep];

  // Register step validator
  const registerValidator = (validator) => {
    setStepValidators(prev => ({ ...prev, [currentStep]: validator }));
  };

  // Handle next step with validation
  const handleNext = () => {
    // Clear previous error
    setValidationError(null);

    // Check if current step has a validator
    const validator = stepValidators[currentStep];
    if (validator) {
      const result = validator();
      if (!result.valid) {
        setValidationError(result.message);
        return; // Don't proceed
      }
    }

    // If on Branch Setup step (step 2), show confirmation modal
    if (currentStep === 2) {
      setShowBranchConfirm(true);
      return;
    }

    if (currentStep < JOURNEY_STEPS.length - 1) {
      nextStep();
    }
  };

  // Proceed after branch confirmation
  const handleBranchConfirm = () => {
    setShowBranchConfirm(false);
    if (currentStep < JOURNEY_STEPS.length - 1) {
      nextStep();
    }
  };

  // Add a new branch from wizard navigation
  const handleAddBranchFromNav = () => {
    setShowBranchConfirm(false);
    const template = selectedTemplate ? getTemplateById(selectedTemplate) : null;
    const newBranch = getDefaultBranch(template);
    addBranch(newBranch);
  };

  // Handle completion
  const handleComplete = (data) => {
    setCreatedData(data);
    setShowCelebration(true);
  };

  // Handle celebration close
  const handleCelebrationClose = (action) => {
    setShowCelebration(false);
    if (onComplete) {
      onComplete(createdData, action);
    }
  };

  // Animation variants for steps
  const stepVariants = {
    initial: { opacity: 0, x: 20 },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -20 },
  };

  if (showCelebration) {
    return <CelebrationScreen data={createdData} onAction={handleCelebrationClose} />;
  }

  return (
    <div>
      {/* Draft Modal */}
      <DraftManager
        isOpen={showDraftModal}
        onClose={() => setShowDraftModal(false)}
        onSelectDraft={(draftId) => {
          loadDraft(draftId);
          setShowDraftModal(false);
        }}
        onStartFresh={() => setShowDraftModal(false)}
      />

      {/* Main Content - No separate header, use WebShell's navigation */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Wizard Title and Actions */}
        <div className="mb-8 flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
              Company Setup Wizard
            </h1>
            <p className="text-gray-500 dark:text-gray-400 mt-2">
              Step {currentStep + 1} of {JOURNEY_STEPS.length}: {JOURNEY_STEPS[currentStep].title}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {isDirty && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => saveDraft()}
                className="text-gray-500 dark:text-gray-400"
              >
                <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                </svg>
                Save Draft
              </Button>
            )}
            {drafts.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowDraftModal(true)}
                className="text-gray-500 dark:text-gray-400"
              >
                <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Drafts ({drafts.length})
              </Button>
            )}
            {onCancel && (
              <Button variant="ghost" size="sm" onClick={onCancel}>
                Cancel
              </Button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Timeline - Left Side on Desktop */}
          <div className="lg:col-span-3 order-2 lg:order-1">
            <div className="sticky top-24">
              <AnimatedTimeline
                steps={JOURNEY_STEPS}
                currentStep={currentStep}
                completedSteps={completedSteps}
                onStepClick={goToStep}
              />
            </div>
          </div>

          {/* Form Area - Center */}
          <div className="lg:col-span-6 order-1 lg:order-2">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentStep}
                variants={stepVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={{ duration: 0.3 }}
              >
                {CurrentStepComponent && (
                  <CurrentStepComponent
                    onNext={handleNext}
                    onPrev={prevStep}
                    onComplete={handleComplete}
                    onValidate={registerValidator}
                    isFirstStep={currentStep === 0}
                    isLastStep={currentStep === JOURNEY_STEPS.length - 1}
                  />
                )}
              </motion.div>
            </AnimatePresence>

            {/* Validation Error Message */}
            {validationError && (
              <div className="mt-4 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg animate-pulse">
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <p className="text-red-700 dark:text-red-300 font-medium">{validationError}</p>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            {currentStep !== JOURNEY_STEPS.length - 1 && (
              <div className="flex justify-between mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
                <div>
                  {currentStep > 0 && (
                    <Button variant="secondary" onClick={prevStep}>
                      <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                      Back
                    </Button>
                  )}
                </div>
                <div className="flex gap-3">
                  {/* Add Branch button - only on Branch Setup step */}
                  {currentStep === 2 && (
                    <Button 
                      variant="secondary" 
                      onClick={handleAddBranchFromNav}
                      className="border-2 border-emerald-500 text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/30"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      Add Branch
                    </Button>
                  )}
                  <Button onClick={handleNext} disabled={isLoading}>
                    {currentStep === 0 ? 'Get Started' : 'Continue'}
                    <svg className="w-4 h-4 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </Button>
                </div>
              </div>
            )}

            {/* Branch Confirmation Modal */}
            <ConfirmBranchesModal
              isOpen={showBranchConfirm}
              onConfirm={handleBranchConfirm}
              onAddMore={handleAddBranchFromNav}
              branchCount={branches.length}
            />
          </div>

          {/* Live Preview - Right Side on Desktop */}
          <div className="lg:col-span-3 order-3">
            <div className="sticky top-24">
              <LivePreview />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Main export with provider wrapper
export default function UnifiedJourneyWizard({ onComplete, onCancel }) {
  return (
    <JourneyProvider>
      <JourneyWizardContent onComplete={onComplete} onCancel={onCancel} />
    </JourneyProvider>
  );
}

